/* eslint-disable no-console */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactFlow, {
  addEdge,
  Background,
  Controls,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
} from 'reactflow';
import {
  PlayArrow as PlayIcon,
  Save as SaveIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';

import ContextMenu from './Context';
import CustomNode from './Custom';
import ConnectionLine from './customLine';
import GroupNode from './Group';
import Items from './items';

import 'reactflow/dist/style.css';
import './index.css';

const nodeTypes = {
  custom: CustomNode,
  group: GroupNode,
};

const getId = () => {
  return `dndnode_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const START_NODE = {
  id: 'start-node',
  type: 'custom',
  position: { x: 100, y: 200 },
  data: { name: 'Start' },
  className: 'custom-node',
  parentNode: '',
};

const DnDFlow = () => {
  const reactFlowWrapper = useRef(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [menu, setMenu] = useState(null);
  const [usedNodes, setUsedNodes] = useState([]);
  const [flowName, setFlowName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const ref = useRef(null);
  const { id } = useParams();
  const navigate = useNavigate();

  // Load existing flow data if editing
  useEffect(() => {
    if (id) {
      const existingFlows = JSON.parse(localStorage.getItem('flows') || '[]');
      const existingFlow = existingFlows.find(flow => flow.id === id);
      if (existingFlow) {
        setNodes(existingFlow.nodes || [START_NODE]);
        setEdges(existingFlow.edges || []);
        setUsedNodes(existingFlow.usedNodes || ['Start']);
        setFlowName(existingFlow.name || '');
      }
    } else {
      // For new flows, ensure Start node is present
      setNodes([START_NODE]);
      setEdges([]);
      setUsedNodes(['Start']);
    }
  }, [id, setNodes, setEdges, setUsedNodes]);

  const onConnect = useCallback(
    params => setEdges(eds => addEdge(params, eds)),
    [setEdges],
  );

  const onNodeContextMenu = useCallback(
    (event, node) => {
      event.preventDefault();
      const pane = ref.current.getBoundingClientRect();

      setMenu({
        id: node.id,
        top: event.clientY < pane.height - 200 && event.clientY,
        left: event.clientX < pane.width - 200 && event.clientX,
        right: event.clientX >= pane.width - 200 && pane.width - event.clientX,
        bottom:
          event.clientY >= pane.height - 200 && pane.height - event.clientY,
        nodeName: node.data?.name,
      });
    },
    [setMenu],
  );

  const onPaneClick = useCallback(() => setMenu(null), [setMenu]);

  const onDragOver = useCallback(event => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    event => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const type = event.dataTransfer.getData('application/reactflow');
      const parentNode = event.dataTransfer.getData(
        'application/reactflow/parent',
      );
      const nodePositionString = event.dataTransfer.getData(
        'application/reactflow/position',
      );
      const nodePosition = nodePositionString
        ? JSON.parse(nodePositionString)
        : null;

      if (typeof type === 'undefined' || !type) {
        return;
      }

      if (usedNodes.includes(type)) {
        return;
      }

      const position = nodePosition
        ? nodePosition
        : reactFlowInstance.project({
            x: event.clientX - reactFlowBounds.left,
            y: event.clientY - reactFlowBounds.top,
          });

      const newNode = {
        id: getId(),
        type: 'custom',
        position: position,
        data: { name: `${type}` },
        className: 'custom-node',
        parentNode: `${parentNode}`,
      };

      setNodes(nds => {
        console.log('Current nodes:', nds);
        console.log('Adding new node:', newNode);
        return nds.concat(newNode);
      });
      setUsedNodes(prev => [...prev, type]);
    },
    [reactFlowInstance, setNodes, usedNodes],
  );

  // Enhanced onNodesChange to handle node deletion via keyboard
  const enhancedOnNodesChange = useCallback(
    changes => {
      // Filter out remove changes for the Start node
      const filteredChanges = changes.filter(change => {
        if (change.type === 'remove' && change.id === 'start-node') {
          return false;
        }
        return true;
      });
      let removedNodeNames = [];
      filteredChanges.forEach(change => {
        if (change.type === 'remove') {
          const node = nodes.find(n => n.id === change.id);
          if (
            node &&
            node.data &&
            node.data.name &&
            node.data.name !== 'Start'
          ) {
            removedNodeNames.push(node.data.name);
          }
        }
      });
      if (removedNodeNames.length > 0) {
        setUsedNodes(prev =>
          prev.filter(name => !removedNodeNames.includes(name)),
        );
      }
      onNodesChange(filteredChanges);
    },
    [nodes, onNodesChange, setUsedNodes],
  );

  const handleSave = () => {
    if (!flowName.trim()) {
      setShowSaveDialog(true);
      return;
    }

    const flowData = {
      id: id || Date.now().toString(),
      name: flowName,
      description: `Flow with ${nodes.length} nodes and ${edges.length} connections`,
      status: 'draft',
      createdAt: new Date().toISOString().split('T')[0],
      updatedAt: new Date().toISOString().split('T')[0],
      nodesCount: nodes.length,
      nodes: nodes,
      edges: edges,
      usedNodes: usedNodes,
    };

    // Get existing flows from localStorage
    const existingFlows = JSON.parse(localStorage.getItem('flows') || '[]');

    // Update or add the flow
    const flowIndex = existingFlows.findIndex(flow => flow.id === flowData.id);
    if (flowIndex >= 0) {
      existingFlows[flowIndex] = flowData;
    } else {
      existingFlows.push(flowData);
    }

    // Save to localStorage
    localStorage.setItem('flows', JSON.stringify(existingFlows));

    // Navigate to list page
    navigate('/flows/list');
  };

  const handleRun = () => {
    // TODO: Implement run functionality
  };

  const handleReset = () => {
    setNodes([START_NODE]);
    setEdges([]);
    setUsedNodes(['Start']);
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      <div className="bg-white border-b border-[#E5E7EB] px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-[#00387D] rounded-lg flex items-center justify-center">
                <SettingsIcon className="text-white text-lg" />
              </div>
              <div>
                <h1 className="text-[26px] font-extrabold tracking-[-0.025em] text-[#0C2340]">
                  Flow Builder
                </h1>
                <p className="text-sm text-[#6B6B6B]">
                  Design and configure your verification workflows
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={handleReset}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <span className="text-sm font-medium">Reset</span>
            </button>
            <button
              onClick={handleRun}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <PlayIcon className="text-sm" />
              <span className="text-sm font-medium">Run Flow</span>
            </button>
            <button
              onClick={handleSave}
              className="flex items-center space-x-2 px-4 py-2 bg-[#00387D] text-white rounded-lg hover:bg-[#002A5F] transition-colors"
            >
              <SaveIcon className="text-sm" />
              <span className="text-sm font-medium">Save</span>
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        <div className="dndflow bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 flex-1 min-h-0 flex flex-col">
          <ReactFlowProvider>
            <div
              className="reactflow-wrapper flex-1 min-h-0"
              ref={reactFlowWrapper}
            >
              <ReactFlow
                ref={ref}
                nodes={nodes}
                edges={edges}
                onNodesChange={enhancedOnNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onInit={setReactFlowInstance}
                onDrop={onDrop}
                nodeTypes={nodeTypes}
                defaultEdgeOptions={{ type: 'lively' }}
                onDragOver={onDragOver}
                connectionLineComponent={ConnectionLine}
                onPaneClick={onPaneClick}
                onNodeContextMenu={onNodeContextMenu}
                fitView
                className="bg-gray-50"
                snapToGrid={true}
                snapGrid={[30, 30]}
                style={{ background: '#f8fafc' }}
              >
                <Controls className="bg-white shadow-lg rounded-lg border border-[#E5E7EB]" />
                <Background
                  color="#94a3b8"
                  gap={30}
                  size={1}
                  className="opacity-80"
                />
                {menu && (
                  <ContextMenu
                    onClick={onPaneClick}
                    setUsedNodes={setUsedNodes}
                    {...menu}
                  />
                )}
              </ReactFlow>
            </div>
          </ReactFlowProvider>
        </div>
        <Items usedNodes={usedNodes} />
      </div>

      {/* Save Dialog */}
      <Dialog open={showSaveDialog} onClose={() => setShowSaveDialog(false)}>
        <DialogTitle>Save Flow</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Flow Name"
            type="text"
            fullWidth
            variant="outlined"
            value={flowName}
            onChange={e => setFlowName(e.target.value)}
            placeholder="Enter flow name..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSaveDialog(false)}>Cancel</Button>
          <Button
            onClick={() => {
              if (flowName.trim()) {
                setShowSaveDialog(false);
                handleSave();
              }
            }}
            variant="contained"
            disabled={!flowName.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DnDFlow;
